import { useState, useEffect } from 'react';
import type { Ayah, TranslationLanguage } from '@/types/domain';
import type { MemorizationMode } from '@/types/memorization';
import { Button } from '@/components/ui/Button';
import { HighlightAllahText } from '@/components/ui/HighlightAllahText';
import { cn } from '@/lib/utils/cn';

/**
 * Displays a single ayah within the memorization workflow.
 *
 * Rendering depends on `mode`:
 *   - 'read'    → Arabic visible, translation visible (per language pref)
 *   - 'repeat'  → same as read; audio is meant to be prominent (parent
 *                 controls audio UI separately)
 *   - 'hide'    → Arabic hidden until the user clicks Reveal. Translation
 *                 also hidden so a glance doesn't spoil the line.
 *   - 'recall'  → everything hidden until Reveal. After reveal, both
 *                 Arabic and translation render together.
 *
 * The "memorized" state is a visual flag on the ayah header, not a
 * separate mode. The parent decides whether the ayah is in the
 * completed set and passes it as `isMemorized`.
 *
 * Each ayah change resets the local "revealed" flag so the next ayah
 * starts hidden in hide/recall modes.
 */

type AyahDisplayProps = {
  ayah: Ayah;
  mode: MemorizationMode;
  translationLanguage: TranslationLanguage;
  showTransliteration: boolean;
  isMemorized: boolean;
};

export function AyahDisplay({
  ayah,
  mode,
  translationLanguage,
  showTransliteration,
  isMemorized,
}: AyahDisplayProps) {
  const [revealed, setRevealed] = useState(false);

  // Reset reveal state when the ayah changes or when leaving hide/recall.
  useEffect(() => {
    setRevealed(false);
  }, [ayah.id, mode]);

  const hideArabic = (mode === 'hide' || mode === 'recall') && !revealed;
  const hideTranslation = (mode === 'hide' || mode === 'recall') && !revealed;

  const showEn =
    !hideTranslation &&
    (translationLanguage === 'en' || translationLanguage === 'both') &&
    !!ayah.translation_en;
  const showUr =
    !hideTranslation &&
    (translationLanguage === 'ur' || translationLanguage === 'both') &&
    !!ayah.translation_ur;

  return (
    <article
      className={cn(
        'py-8 px-5 sm:px-8 rounded-2xl',
        'border transition-colors',
        isMemorized
          ? 'border-accent/40 bg-accent/5'
          : 'border-ink-200/60 dark:border-ink-800/60 bg-white/40 dark:bg-ink-900/40',
      )}
    >
      <header className="flex items-center justify-between mb-5">
        <span
          aria-label={`Surah ${ayah.surah_id}, Ayah ${ayah.ayah_number}`}
          className="font-serif text-sm text-ink-400 dark:text-ink-500 num-tight"
        >
          {ayah.surah_id}:{ayah.ayah_number}
        </span>
        {isMemorized && (
          <span
            className="text-xs uppercase tracking-wide text-accent-600 dark:text-accent-400 font-medium"
            aria-label="Marked as memorized"
          >
            ✓ Memorized
          </span>
        )}
      </header>

      {hideArabic ? (
        <RevealPanel
          mode={mode}
          onReveal={() => setRevealed(true)}
        />
      ) : (
        <>
          <p
            className={cn(
              'arabic-display mb-5',
              hideArabic && 'opacity-0',
            )}
          >
            <HighlightAllahText text={ayah.text_ar} />
          </p>

          {showTransliteration && ayah.arabic_roman && (
            <p className="text-sm italic leading-relaxed text-ink-500 dark:text-ink-400 mb-4">
              {ayah.arabic_roman}
            </p>
          )}

          {showEn && (
            <p
              className={cn(
                'font-serif text-lg leading-relaxed text-ink-800 dark:text-ink-100',
                showUr && 'mb-4',
              )}
            >
              <HighlightAllahText text={ayah.translation_en} />
            </p>
          )}

          {showUr && ayah.translation_ur && (
            <p className="urdu-display"><HighlightAllahText text={ayah.translation_ur} /></p>
          )}
        </>
      )}
    </article>
  );
}

function RevealPanel({
  mode,
  onReveal,
}: {
  mode: MemorizationMode;
  onReveal: () => void;
}) {
  return (
    <div className="py-10 text-center">
      <p className="text-sm text-ink-500 dark:text-ink-400 mb-5 leading-relaxed">
        {mode === 'hide'
          ? 'The Arabic is hidden. Listen to the audio, then reveal to verify.'
          : 'Try to recall this ayah from memory. Reveal when ready.'}
      </p>
      <Button variant="secondary" onClick={onReveal} size="md">
        Reveal
      </Button>
    </div>
  );
}
